import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  IconButton
} from '@mui/material';
import { logger } from "../../../utils/logger";
import CloseIcon from '@mui/icons-material/Close';
import FavoriteIcon from '@mui/icons-material/Favorite';
import { nowISO } from '@/utils/dateUtils';
import { post } from '@/utils/apiClient';
import { useIsMobile } from '../../../hooks/useMediaQuery';

/**
 * Reflection modal for end-of-day closure ritual (4B) and year-end reflection (6B).
 * Provides a simple, supportive interface for therapist self-reflection.
 */
function ReflectionModal({ 
  open, 
  onClose, 
  type = 'closure', // 'closure' or 'year_reflection'
  impactMessage = null,
  onSubmit = () => {},
}) {
  const isMobile = useIsMobile();
  const [responses, setResponses] = useState({
    question1: '',
    question2: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const isYearEnd = type === 'year_reflection';

  const questions = isYearEnd ? {
    question1: "What work felt most meaningful to you this year?",
    question2: "What are your goals or hopes for the coming year?",
  } : {
    question1: "Was there a session that stayed with you today?",
    question2: "Anything you want to remember for tomorrow?",
  };

  const title = isYearEnd ? "Year-End Reflection" : "End-of-Day Reflection";

  const handleChange = (field) => (event) => {
    setResponses(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      if (onSubmit) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        await (onSubmit as any)({
          type,
          responses,
          timestamp: nowISO(),
        });
      }
      onClose();
    } catch (error) {
      logger.error('Error saving reflection:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDismiss = async () => {
    try {
      // Call the dismiss-ritual API to mark as seen for today
      await post('/api/therapist_dashboard/briefing/dismiss-ritual', { ritual_type: type });
    } catch (error) {
      logger.error('Error dismissing ritual:', error);
    }
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleDismiss}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { ...(!isMobile && { borderRadius: 2 }) }
      }}
     fullScreen={isMobile}>
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        pb: 1,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FavoriteIcon color={isYearEnd ? 'info' : 'primary'} />
          <Typography variant="h6" fontWeight={600}>
            {title}
          </Typography>
        </Box>
        <IconButton size="small" onClick={handleDismiss} aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {/* Impact message for year-end reflection */}
        {isYearEnd && impactMessage && (
          <Box sx={{ 
            mb: 3, 
            p: 2, 
            bgcolor: 'info.50', 
            borderRadius: 1,
            border: 1,
            borderColor: 'info.100',
          }}>
            <Typography variant="body2" color="text.secondary">
              {impactMessage}
            </Typography>
          </Box>
        )}

        {/* Intro text */}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {isYearEnd 
            ? "Take a moment to reflect on the past year and look ahead."
            : "Take a moment to close out your day with intention."
          }
        </Typography>

        {/* Question 1 */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            {questions.question1}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Your thoughts..."
            value={responses.question1}
            onChange={handleChange('question1')}
            variant="outlined"
            size="small"
          />
        </Box>

        {/* Question 2 */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            {questions.question2}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Your thoughts..."
            value={responses.question2}
            onChange={handleChange('question2')}
            variant="outlined"
            size="small"
          />
        </Box>

        <Typography variant="caption" color="text.secondary">
          This reflection is just for you. It won&apos;t be shared with anyone.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button 
          onClick={handleDismiss}
          color="inherit"
        >
          Skip for today
        </Button>
        <Button 
          onClick={handleSubmit}
          variant="contained"
          disabled={submitting}
        >
          {submitting ? 'Saving...' : 'Save reflection'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ReflectionModal;
